import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserMinus, Trash2, AlertTriangle, Loader2, Users, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function Dropped() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // { student }
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dropped");
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const dropAllRemaining = async (row) => {
    setBusy(`${row.contact_id}::${row.student_name}`);
    try {
      const { data } = await api.post("/dropped/drop-all-for-student", { contact_id: row.contact_id, student_name: row.student_name });
      toast.success(`${row.student_name}: dropped from ${data.dropped_count} more class(es)${data.family_moved_to_inactive ? " · family moved to Inactive" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const clearAll = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/dropped/clear");
      toast.success(`Cleared ${data.cleared_count} reviewed drop(s) from the queue`);
      setConfirmClear(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClearing(false); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="dropped-page">
      <div className="max-w-6xl mx-auto p-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Dropped</h1>
            <p className="text-sm text-slate-500 mt-1">
              {rows.length === 0 ? "No pending drops to review." :
                `${rows.length} student${rows.length === 1 ? "" : "s"} recently dropped. Verify each is out of all classes, then Clear when done.`}
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => setConfirmClear(true)}
            disabled={rows.length === 0}
            className="border-slate-300"
            data-testid="clear-dropped-button"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Clear Queue
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading...</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg py-16 text-center">
            <UserMinus className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <div className="text-sm font-medium text-slate-700">All clear!</div>
            <div className="text-xs text-slate-500 mt-1">No drops to review right now. New drops from Zapier will appear here.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => {
              const busyKey = `${row.contact_id}::${row.student_name}`;
              const isBusy = busy === busyKey;
              const remaining = row.remaining_active_count || 0;
              return (
                <div key={busyKey} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`dropped-row-${row.contact_id}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-slate-900 text-lg leading-tight">{row.student_name}</h3>
                        {remaining > 0 && (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100 text-[11px]">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Still in {remaining} class{remaining === 1 ? "" : "es"}
                          </Badge>
                        )}
                        {remaining === 0 && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100 text-[11px]">
                            ✓ Fully dropped
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                        <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {row.contact_name || "(no parent)"}</span>
                        {row.phone && <span className="font-mono">{row.phone}</span>}
                        <span>Last drop: {row.latest_dropped_at ? new Date(row.latest_dropped_at).toLocaleString() : "—"}</span>
                      </div>
                    </div>
                    {remaining > 0 && (
                      <Button
                        size="sm"
                        onClick={() => dropAllRemaining(row)}
                        disabled={isBusy}
                        className="bg-[#D9232E] hover:bg-[#B91C25] text-white shrink-0"
                        data-testid={`drop-all-btn-${row.contact_id}`}
                      >
                        {isBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Drop from remaining {remaining}</>}
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="bg-red-50 border border-red-100 rounded-md p-3">
                      <div className="text-[11px] font-semibold text-red-800 uppercase tracking-wide mb-2 flex items-center gap-1">
                        <UserMinus className="w-3 h-3" /> Dropped from ({row.dropped_classes.length})
                      </div>
                      <ul className="space-y-1">
                        {row.dropped_classes.map((c) => (
                          <li key={c.enrollment_id} className="text-xs text-slate-700 flex items-center gap-1.5">
                            <GraduationCap className="w-3 h-3 text-red-600 shrink-0" /> {c.class_name}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-md p-3">
                      <div className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1">
                        Still enrolled in ({remaining})
                      </div>
                      {remaining === 0 ? (
                        <div className="text-xs text-slate-400 italic">Nothing to worry about.</div>
                      ) : (
                        <ul className="space-y-1">
                          {row.remaining_active_classes.map((c) => (
                            <li key={c.enrollment_id} className="text-xs text-slate-700 flex items-center gap-1.5">
                              <GraduationCap className="w-3 h-3 text-slate-500 shrink-0" /> {c.class_name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
          <DialogContent data-testid="clear-dropped-dialog">
            <DialogHeader>
              <DialogTitle>Clear the Dropped queue?</DialogTitle>
              <p className="text-sm text-slate-500">
                This marks every drop currently on this page as reviewed. They stay dropped from their classes — they just disappear from this list. Do this weekly.
              </p>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmClear(false)}>Cancel</Button>
              <Button onClick={clearAll} disabled={clearing} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="confirm-clear-dropped">
                {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : `Clear ${rows.length}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
